/**
 * Condition Utilities for File Matcher Module
 */

"use strict";

import { debugMode } from './constants.js';

/**
 * Check if match condition is a "rule-based" match (Synonym, Roman, Regex, Phrase)
 * Rule-based matches should be protected from fuzzy filtering.
 */
export function isRuleBasedMatch(item) {
    if (!item) return false;
    // Nested condition
    if (typeof item === 'object' && item.condition) {
        return hasRuleBasedPattern(item);
    }
    // Pattern object
    const boost = (typeof item === 'object' && item.boost) ? item.boost : 1;
    return boost > 1;
}

/**
 * Recursively check if condition contains rule-based patterns (boost > 1)
 */
export function hasRuleBasedPattern(condition) {
    if (!condition || !condition.value) return false;
    
    if (Array.isArray(condition.value)) {
        for (const item of condition.value) {
            if (typeof item === 'object' && item.pattern !== undefined) {
                if ((item.boost || 1) > 1) return true;
            } else if (typeof item === 'object' && item.condition) {
                if (hasRuleBasedPattern(item)) return true;
            }
        }
    }
    return false;
}

/**
 * Split conditions into protected (boost > 1) and regular (boost = 1) conditions
 * @param {Array} matchCond - Original match conditions
 * @returns {Object} { exactPhraseConditions, regularConditions, wasOrWrapper }
 */
export function splitConditionsByBoost(matchCond) {
    const exactPhraseConditions = []; // These are protected results
    const regularConditions = [];
    let wasOrWrapper = false;
    
    if (debugMode === '1') {
        console.log('[splitConditionsByBoost] Input conditions:', JSON.stringify(matchCond, null, 2).substring(0, 500));
    }
    
    for (const level1 of matchCond) {
        let isProtected = false;
        
        if (level1.condition === "or" && level1.value && level1.value.length > 0) {
            const isOrWrapper = level1.value[0] && level1.value[0].condition && 
                (level1.value[0].condition === "and" || level1.value[0].condition === "or");
            
            if (isOrWrapper) {
                wasOrWrapper = true;
                let protectedAndCond = null;
                let regularAndCond = null;
                
                for (const nestedCond of level1.value) {
                    if (nestedCond.condition === "and" && nestedCond.value && Array.isArray(nestedCond.value)) {
                        // Check if this is HIGH PRIORITY (exact phrase with boost 5) or LOW PRIORITY (individual terms)
                        // HIGH PRIORITY: has exact phrase pattern with boost 5 (BOOST_EXACT_PHRASE_AUTO)
                        // LOW PRIORITY: individual terms, even if some have boost > 1 (like Roman numerals)
                        const hasExactPhraseBoost5 = nestedCond.value.some(orCond => {
                            if (orCond.condition === "or" && orCond.value && Array.isArray(orCond.value)) {
                                return orCond.value.some(pattern => {
                                    const boost = (pattern && typeof pattern === 'object' && pattern.boost) ? pattern.boost : 1;
                                    return boost >= 5; // BOOST_EXACT_PHRASE_AUTO = 5
                                });
                            }
                            return false;
                        });
                        
                        if (hasExactPhraseBoost5) {
                            protectedAndCond = nestedCond;
                        } else {
                            regularAndCond = nestedCond;
                        }
                    }
                }
                
                if (protectedAndCond) {
                    exactPhraseConditions.push(protectedAndCond);
                    if (debugMode === '1') {
                        console.log('[splitConditionsByBoost] Added HIGH PRIORITY condition (exact phrase, boost 5)');
                    }
                }
                if (regularAndCond) {
                    regularConditions.push(regularAndCond);
                    if (debugMode === '1') {
                        console.log('[splitConditionsByBoost] Added LOW PRIORITY condition (individual terms)');
                    }
                }
                continue;
            } else {
                for (const patternItem of level1.value) {
                    if ((patternItem.boost || 1) > 1) {
                        isProtected = true;
                        break;
                    }
                }
            }
        } else if (level1.condition === "and" && level1.value && level1.value.length > 0) {
            const protectedOrConditions = [];
            const regularOrConditions = [];
            
            for (const level2 of level1.value) {
                if (level2.condition === "or" && level2.value && Array.isArray(level2.value)) {
                    if (hasRuleBasedPattern(level2)) {
                        protectedOrConditions.push(level2);
                        isProtected = true;
                    } else {
                        regularOrConditions.push(level2);
                    }
                } else {
                    regularOrConditions.push(level2);
                }
            }
            
            if (protectedOrConditions.length > 0) {
                exactPhraseConditions.push({
                    condition: "and",
                    value: protectedOrConditions,
                    isCategory: level1.isCategory
                });
            }
            if (regularOrConditions.length > 0) {
                regularConditions.push({
                    condition: "and",
                    value: regularOrConditions,
                    isCategory: level1.isCategory
                });
            }
            continue;
        }
        
        if (isProtected || hasRuleBasedPattern(level1)) {
            exactPhraseConditions.push(level1);
        } else {
            regularConditions.push(level1);
        }
    }
    
    
    if (debugMode === '1') {
        console.log('[splitConditionsByBoost] Result:', {
            highPriority: exactPhraseConditions.length,
            lowPriority: regularConditions.length,
            wasOrWrapper: wasOrWrapper
        });
    }
    
    return { exactPhraseConditions, regularConditions, wasOrWrapper };
}

/**
 * Recursive function to extract all patterns from nested conditions
 * @param {Array} conditions - Search conditions
 * @param {string} modifier - Default modifier
 * @returns {Array} Array of patterns
 */
export function extractPatterns(conditions, modifier = 'i') {
    const patterns = [];
    if (!conditions || !Array.isArray(conditions)) return patterns;
    
    for (const cond of conditions) {
        if (!cond || !cond.value) continue;
        
        for (const item of cond.value) {
            if (typeof item === 'string') {
                patterns.push({ pattern: item, modifier: cond.modifier || modifier });
            } else if (typeof item === 'object' && item.pattern) {
                patterns.push({ 
                    pattern: item.pattern, 
                    modifier: cond.modifier || modifier,
                    flags: (item.flags !== undefined && item.flags !== null) 
                        ? item.flags 
                        : (cond.modifier || modifier)
                });
            } else if (typeof item === 'object' && item.condition && item.value) {
                patterns.push(...extractPatterns([item], cond.modifier || modifier));
            }
        }
    }
    return patterns;
}
